// src/components/forms/fieldsConfig/devisFields.js

// Champs pour la création d'un devis
export const devisCreationFields = [
  { 
    name: 'clientId', 
    label: 'Client', 
    type: 'select', 
    required: true,
    options: [], // Rempli dynamiquement
    placeholder: 'Sélectionner un client',
    helpText: 'Le client est obligatoire pour créer un devis'
  },
  { 
    name: 'name', 
    label: 'Titre du devis', 
    type: 'text', 
    placeholder: 'Ex: Devis pour installation électrique',
    helpText: 'Nom interne pour identifier le devis',
    col: 6
  },
  { 
    name: 'dateValidite', 
    label: 'Date de validité', 
    type: 'date', 
    required: true,
    helpText: 'Date jusqu\'à laquelle le devis est valable',
    col: 6
  },
  { 
    name: 'commercialId', 
    label: 'Commercial', 
    type: 'select', 
    options: [], // Rempli dynamiquement
    placeholder: 'Assigner un commercial',
    col: 6
  },
  { 
    name: 'priorite', 
    label: 'Priorité', 
    type: 'select', 
    options: [
      { value: 'basse', label: 'Basse' },
      { value: 'moyenne', label: 'Moyenne' },
      { value: 'haute', label: 'Haute' },
      { value: 'urgente', label: 'Urgente' }
    ],
    defaultValue: 'moyenne',
    col: 6
  },
  { 
    name: 'probabilite', 
    label: 'Probabilité de succès (%)', 
    type: 'number', 
    min: 0,
    max: 100,
    defaultValue: 50,
    helpText: 'Estimation de la probabilité que le devis soit accepté',
    col: 6
  },
  { 
    name: 'notesClient', 
    label: 'Notes pour le client', 
    type: 'textarea', 
    rows: 3,
    placeholder: 'Notes ou instructions spécifiques pour le client...'
  },
  { 
    name: 'termsAndConditions', 
    label: 'Conditions générales', 
    type: 'textarea', 
    rows: 4,
    placeholder: 'Conditions de paiement, garanties, etc...'
  },
  { 
    name: 'paymentMethods', 
    label: 'Moyens de paiement acceptés', 
    type: 'textarea', 
    rows: 2,
    placeholder: 'CB, virement, chèque, espèces...'
  }
];

// Champs pour la mise à jour d'un devis
export const devisUpdateFields = [
  ...devisCreationFields,
  { 
    name: 'statut', 
    label: 'Statut', 
    type: 'select', 
    required: true,
    options: [
      { value: 'brouillon', label: 'Brouillon' },
      { value: 'envoyé', label: 'Envoyé' },
      { value: 'accepté', label: 'Accepté' },
      { value: 'refusé', label: 'Refusé' },
      { value: 'transformé_commande', label: 'Transformé en commande' },
      { value: 'expiré', label: 'Expiré' }
    ],
    defaultValue: 'brouillon',
    col: 6
  },
  { 
    name: 'raisonRefus', 
    label: 'Raison du refus', 
    type: 'textarea', 
    rows: 2,
    placeholder: 'Si le devis est refusé, indiquez la raison...',
    helpText: 'Uniquement si le statut est "refusé"',
    col: 6
  }
];

// Champs pour les paramètres de calcul
export const devisCalculFields = [
  { 
    name: 'remiseType', 
    label: 'Type de remise', 
    type: 'select', 
    options: [
      { value: 'Aucune', label: 'Aucune remise' },
      { value: 'Pourcentage', label: 'Pourcentage' },
      { value: 'Montant', label: 'Montant fixe' }
    ],
    defaultValue: 'Aucune',
    col: 6
  },
  { 
    name: 'remiseValue', 
    label: 'Valeur de la remise', 
    type: 'number', 
    min: 0,
    defaultValue: 0,
    placeholder: '0',
    helpText: 'Pourcentage ou montant selon le type sélectionné',
    col: 6
  },
  { 
    name: 'tva', 
    label: 'Taux TVA (%)', 
    type: 'number', 
    min: 0,
    max: 100,
    defaultValue: 10,
    step: 0.1,
    col: 6
  }
];

// Fonction pour formater les données devis
export const formatDevisData = (formData) => {
  const formattedData = {
    clientId: formData.clientId,
    name: formData.name?.trim(),
    dateValidite: formData.dateValidite,
    commercialId: formData.commercialId || undefined,
    priorite: formData.priorite,
    probabilite: parseFloat(formData.probabilite) || 50,
    notesClient: formData.notesClient?.trim(),
    termsAndConditions: formData.termsAndConditions?.trim(),
    paymentMethods: formData.paymentMethods?.trim(),
    // Paramètres de calcul
    remiseType: formData.remiseType || 'Aucune',
    remiseValue: parseFloat(formData.remiseValue) || 0,
    tva: parseFloat(formData.tva) || 10
  };

  // Si c'est une mise à jour, inclure le statut
  if (formData.statut) {
    formattedData.statut = formData.statut;
    if (formData.statut === 'refusé' && formData.raisonRefus) {
      formattedData.raisonRefus = formData.raisonRefus.trim();
    }
  }

  return formattedData;
};

// Fonction pour pré-remplir les données devis
export const prepareDevisFormData = (devis) => {
  if (!devis) return {};
  
  return {
    clientId: devis.clientId?._id || devis.clientId || '',
    name: devis.name || '',
    dateValidite: devis.dateValidite ? new Date(devis.dateValidite).toISOString().split('T')[0] : '',
    commercialId: devis.commercialId?._id || devis.commercialId || '',
    priorite: devis.priorite || 'moyenne',
    probabilite: devis.probabilite || 50,
    notesClient: devis.notesClient || '',
    termsAndConditions: devis.termsAndConditions || '',
    paymentMethods: devis.paymentMethods || '',
    statut: devis.statut || 'brouillon',
    raisonRefus: devis.raisonRefus || '',
    // Paramètres de calcul
    remiseType: devis.remiseType || 'Aucune',
    remiseValue: devis.remiseValue || 0,
    tva: devis.tva || 10
  };
};

// Fonction de validation
export const validateDevisForm = (formData) => {
  const errors = {};
  
  if (!formData.clientId) {
    errors.clientId = 'Le client est obligatoire';
  }
  
  if (!formData.dateValidite) {
    errors.dateValidite = 'La date de validité est obligatoire';
  } else {
    const validiteDate = new Date(formData.dateValidite);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (validiteDate < today) {
      errors.dateValidite = 'La date de validité est dans le passé';
    }
  }
  
  if (formData.remiseType === 'Pourcentage' && formData.remiseValue > 100) {
    errors.remiseValue = 'Le pourcentage de remise ne peut pas dépasser 100%';
  }
  
  if (formData.tva && (formData.tva < 0 || formData.tva > 100)) {
    errors.tva = 'Le taux TVA doit être entre 0 et 100%';
  }
  
  return errors;
};

export default {
  creation: devisCreationFields,
  update: devisUpdateFields,
  calcul: devisCalculFields,
  format: formatDevisData,
  prepare: prepareDevisFormData,
  validate: validateDevisForm
};